import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './assets/techwise-logo.png';
import googleIcon from './assets/google-icon.png';
import './theme.css';

const Signin: React.FC = () => {
  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [hoverSignIn, setHoverSignIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false);

  // Signup state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);

  // Tab state
  const [isLogin, setIsLogin] = useState(true);

  const navigate = useNavigate();
  const BACKEND_URL = "https://gln7w8w5uc.execute-api.ap-south-1.amazonaws.com/dev";
  //const BACKEND_URL = "http://localhost:5000";


  // Login handler
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    setShowLoadingAnimation(true);
    try {
      const res = await fetch(`${BACKEND_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Login failed');
      }
      setTimeout(() => {
        setShowLoadingAnimation(false);
        navigate('/app');
      }, 1500);
    } catch (err: any) {
      setLoginError(err.message || 'Login failed');
      setShowLoadingAnimation(false);
    } finally {
      setLoading(false);
    }
  };

  // Signup handler
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');
    setSignupSuccess('');
    setSignupLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: signupName, email: signupEmail, password: signupPassword }),
      });
      if (!res.ok) throw new Error('Signup failed');
      const data = await res.json();
      setSignupSuccess(data.message || 'Signup successful!');
      setTimeout(() => {
        setIsLogin(true); // Switch to login tab after signup
      }, 1200);
    } catch (err: any) {
      setSignupError(err.message || 'Signup failed');
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 400, margin: '2rem auto', padding: 20, background: 'var(--background-color)', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
      {showLoadingAnimation ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}>
          <div className="loader"></div>
        </div>
      ) : (
        <>
          {/* Header with Logo */}
          <header style={{ display: 'flex', justifyContent: 'center', paddingBottom: 20 }}>
            <img src={logo} alt="TechWise Logo" style={{ height: 40 }} />
          </header>

          {/* Tabs */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
            <button
              onClick={() => setIsLogin(true)}
              style={{
                flex: 1,
                padding: '8px 16px',
                border: 'none',
                background: 'none',
                color: isLogin ? 'var(--primary-color)' : '#888',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderBottom: isLogin ? '2px solid var(--primary-color)' : '2px solid transparent',
                transition: 'color 0.2s, border-bottom 0.2s',
              }}
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              style={{
                flex: 1,
                padding: '8px 16px',
                border: 'none',
                background: 'none',
                color: !isLogin ? 'var(--primary-color)' : '#888',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderBottom: !isLogin ? '2px solid var(--primary-color)' : '2px solid transparent',
                transition: 'color 0.2s, border-bottom 0.2s',
              }}
            >
              Sign Up
            </button>
          </div>

          {/* Login Form */}
          {isLogin && (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="email" style={{ display: 'block', marginBottom: 4 }}>Email:</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  style={{
                    width: '92%',
                    padding: '12px 16px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    fontSize: 16,
                  }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="password" style={{ display: 'block', marginBottom: 4 }}>Password:</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{
                    width: '92%',
                    padding: '12px 16px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    fontSize: 16,
                  }}
                />
              </div>
              {loginError && <div style={{ color: 'red', marginBottom: 12 }}>{loginError}</div>}
              <button
                type="submit"
                disabled={loading}
                onMouseEnter={() => setHoverSignIn(true)}
                onMouseLeave={() => setHoverSignIn(false)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  background: hoverSignIn ? '#000' : '#fff',
                  color: hoverSignIn ? '#fff' : '#C54040',
                  fontWeight: 'bold',
                  fontSize: 16,
                  cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                {loading ? 'Logging in...' : 'Log in'}
              </button>
            </form>
          )}

          {/* Signup Form */}
          {!isLogin && (
            <form onSubmit={handleSignup}>
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="signupName" style={{ display: 'block', marginBottom: 4 }}>Name:</label>
                <input
                  id="signupName"
                  type="text"
                  value={signupName}
                  onChange={e => setSignupName(e.target.value)}
                  required
                  style={{
                    width: '92%',
                    padding: '12px 16px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    fontSize: 16,
                  }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="signupEmail" style={{ display: 'block', marginBottom: 4 }}>Email:</label>
                <input
                  id="signupEmail"
                  type="email"
                  value={signupEmail}
                  onChange={e => setSignupEmail(e.target.value)}
                  required
                  style={{
                    width: '92%',
                    padding: '12px 16px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    fontSize: 16,
                  }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label htmlFor="signupPassword" style={{ display: 'block', marginBottom: 4 }}>Password:</label>
                <input
                  id="signupPassword"
                  type="password"
                  value={signupPassword}
                  onChange={e => setSignupPassword(e.target.value)}
                  required
                  style={{
                    width: '92%',
                    padding: '12px 16px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    fontSize: 16,
                  }}
                />
              </div>
              {signupError && <div style={{ color: 'red', marginBottom: 12 }}>{signupError}</div>}
              {signupSuccess && <div style={{ color: 'green', marginBottom: 12 }}>{signupSuccess}</div>}
              <button
                type="submit"
                disabled={signupLoading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  background: '#fff',
                  color: '#C54040',
                  fontWeight: 'bold',
                  fontSize: 16,
                  cursor: 'pointer',
                }}
              >
                {signupLoading ? 'Signing up...' : 'Create Account'}
              </button>
            </form>
          )}

          {/* Log in with Google Button */}
          <button
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 4,
              border: '1px solid #ccc',
              background: '#fff',
              color: '#000',
              fontWeight: 'bold',
              fontSize: 16,
              cursor: 'pointer',
              marginTop: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <img src={googleIcon} alt="Google Icon" style={{ width: 20, height: 20 }} />
            Log in with Google
          </button>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button
              style={{
                background: 'none',
                border: 'none',
                color: '#000000ff',
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Forgot password?
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Signin;